/**
 * SITE CONTENT — easy to edit.
 * Change any text below and the website updates everywhere.
 * No code knowledge needed: just replace the text inside the quotes.
 */

export const site = {
  brand: {
    name: "HawkEdge AI Advisory",
    short: "HawkEdge",
    tagline: "Strategic clarity for the AI era.",
    address: "1 Park Avenue, Suite 4500, New York, NY",
    email: "hello@hawkedge.ai",
    phone: "+1 (212) 555-0144",
    logo:
      "https://customer-assets.emergentagent.com/job_digital-advisor-pro/artifacts/l758fca6_Hawk_flying_honing_object_202604301646.jpeg",
  },

  // Top navigation links — the order here is the order on the site.
  nav: [
    { label: "Home", href: "/" },
    { label: "Services", href: "/services" },
    { label: "Industries", href: "/industries" },
    { label: "Insights", href: "/insights" },
    { label: "About", href: "/about" },
    { label: "Contact", href: "/contact" },
  ],

  // ABOUT (also serves as the landing page at "/")
  about: {
    eyebrow: "About",
    title: "Strategy. Transformation. Measurable enterprise value.",
    subtitle:
      "HawkEdge AI Advisory was founded to help executive leaders tackle the defining business challenge of our time: harnessing AI strategy and adoption, business intelligence, and data analytics to build organizations that are faster, smarter, and more resilient.",
    ctaPrimary: { label: "Request a consultation", href: "/contact" },
    ctaSecondary: { label: "View services", href: "/services" },
    brandImage:
      "https://customer-assets.emergentagent.com/job_digital-advisor-pro/artifacts/6z0e5nno_My_Brand_Hawk_Flying_Over_The_Globe.jpeg",

    founder: {
      eyebrow: "Founder",
      name: "Lenny Finkel",
      role: "Founder, Managing Director, HawkEdge AI Advisory",
      discipline: "Enterprise AI Strategy & Transformation Advisor",
      monogram: "LF",
      photo:
        "https://customer-assets.emergentagent.com/job_digital-advisor-pro/artifacts/bc6i14g7_20240319_173122.jpg",
      bio: [
        "With more than 15 years at the intersection of enterprise strategy, data modernization, and organizational transformation, Lenny Finkel brings a practitioner's depth — and a strategist's creativity — to every engagement.",
        "What sets HawkEdge AI Advisory apart is a commitment to addressing business and technical pain points not just at the surface, but strategically and creatively at their root — transforming them into long-term, revenue-generating and cost-cutting solutions that compound in value over time.",
        "Lenny has held senior advisory and transformation leadership roles across a broad range of industries, bringing domain fluency that few advisors can match.",
      ],
    },

    industriesServedEyebrow: "Industries served",
    industriesServed: [
      "Banking",
      "Financial Services",
      "SaaS",
      "Real Estate",
      "Media",
      "Education",
      "Manufacturing",
    ],

    rolesEyebrow: "Roles & remit",
    rolesParagraph:
      "Across every engagement — whether serving as a Global Strategy & Transformation Leader, Managing Director of Analytics Advisory, Enterprise Modernization Consultant, or Analytics & Automation Leader — his work has centered on translating complex strategic priorities into operating models, governance frameworks, and AI-enabled transformation roadmaps that deliver measurable, lasting outcomes.",

    quote:
      "I work at the intersection of strategy, transformation execution, and measurable enterprise value — helping organizations align business priorities, technology investment, and AI capability to drive sustainable growth.",
    quoteAttribution: "Lenny Finkel, Founder/Managing Director",

    statsEyebrow: "Impact by the numbers",
    stats: [
      { value: "$12M+", label: "Consulting & transformation portfolio managed" },
      { value: "25%", label: "Productivity improvement through analytics-led modernization" },
      { value: "35%", label: "Reduction in enterprise platform fragmentation" },
      { value: "30+", label: "Cross-functional professionals led on transformation programs" },
    ],

    differentiator: {
      eyebrow: "Our Differentiator",
      title: "Intent-Based AI: From Vision to Verified Outcomes.",
      body:
        "At HawkEdge AI Advisory, we go beyond strategy decks. We design and implement Intent-Based AI solutions — systems purpose-built around your organization's specific business intent, whether that's reducing cost, accelerating decisions, elevating customer experience, or driving operational efficiency. Drawing on the same methodology used by the most sophisticated AI advisory practices in the U.S., we bridge the gap between AI ambition and production-grade deployment — ensuring every initiative is commercially viable, technically sound, compliant by design, and built to scale.",
    },

    deliverablesEyebrow: "What we deliver",
    deliverablesTitle: "Outcomes, engineered.",
    deliverables: [
      { icon: "TrendingDown", title: "Cost Reduction" },
      { icon: "Zap", title: "Faster Decision-Making" },
      { icon: "TrendingUp", title: "Increase Revenue Generation" },
      { icon: "Cog", title: "Operational Efficiency" },
    ],

    practitionerEyebrow: "A practitioner's perspective on AI",
    practitionerTitle: "Operator-grade insight, not generic theory.",
    practitionerPoints: [
      "Negotiated with a major data-visualization vendor to incorporate AI capabilities directly into their cloud-based analytics platform — securing capabilities that elevated the entire BI and reporting function.",
      "Secured senior leadership buy-in for AI-integrated vendor contracts by building a clear business case around analytics, reporting, and self-service intelligence.",
      "Coordinated targeted AI capability demonstrations — focused on dashboard creation, KPI generation, and workflow automation — to assess fit for enterprise data and analytics teams.",
      "Proposed and championed AI-powered workflow automation to senior management, connecting operational efficiency gains to measurable enterprise outcomes.",
    ],

    credentialsEyebrow: "Credentials & education",
    credentials: [
      { school: "MIT", program: "Applied AI & Data Science" },
      { school: "Pace University", program: "B.B.A." },
      { school: "Setfocus", program: "Microsoft BI Certification" },
      { school: "Chiron Business Institute", program: "Business Analysis" },
    ],

    locationLine:
      "Based in Lakewood, NJ — serving enterprise clients across the Tri-State Area and nationally.",
  },

  // SERVICES
  services: {
    eyebrow: "Services",
    title: "What We Do",
    intro:
      "Strategy, implementation, and operational support — not just slide decks.",

    banner: {
      eyebrow: "Our Differentiator",
      title: "Intent-Based AI: From Vision to Verified Outcomes.",
      body:
        "We design and implement Intent-Based AI solutions — purpose-built around your organization's specific business intent. Every engagement is scoped to deliver commercially viable, technically sound, and compliance-ready outcomes that scale.",
    },

    deliverablesEyebrow: "What we deliver",
    deliverables: [
      { icon: "TrendingDown", title: "Cost Reduction" },
      { icon: "Zap", title: "Faster Decision-Making" },
      { icon: "TrendingUp", title: "Increase Revenue Generation" },
      { icon: "Cog", title: "Operational Efficiency" },
    ],

    itemsEyebrow: "Core Advisory Services",
    itemsTitle:
      "Comprehensive AI advisory across core disciplines, with capabilities that scale with you.",
    items: [
      {
        icon: "Compass",
        title: "AI Strategy & Enablement",
        description:
          "Defining enterprise-wide AI roadmaps, identifying high-ROI use cases, sequencing them into actionable plans, and building the governance structures to make AI adoption measurable and sustainable.",
      },
      {
        icon: "Gauge",
        title: "AI Readiness Assessment",
        description:
          "Evaluating your organization's data quality, infrastructure, security posture, and governance maturity to establish a clear baseline before committing to AI investment.",
      },
      {
        icon: "Sparkles",
        title: "Generative AI, Copilots & Agents",
        description:
          "Designing and deploying generative AI solutions — including enterprise chatbots, AI copilots, RAG pipelines, and autonomous agents — tailored to your workflows and integrated into your systems.",
      },
      {
        icon: "Brain",
        title: "Machine Learning & Predictive Analytics",
        description:
          "Building custom ML models, NLP systems, and predictive analytics solutions that turn enterprise data into forward-looking intelligence and competitive advantage.",
      },
      {
        icon: "Cpu",
        title: "MLOps & LLMOps",
        description:
          "Establishing the infrastructure, monitoring, and lifecycle management practices needed to deploy AI models reliably — and keep them performing at scale over time.",
      },
      {
        icon: "Database",
        title: "Data Engineering & Cloud Modernization",
        description:
          "Modernizing your data stack — including lakehouse architecture, cloud migration, and pipeline design — to create the data foundation that enterprise AI requires.",
      },
      {
        icon: "Workflow",
        title: "Workflow Automation & Process Redesign",
        description:
          "Reimagining and automating business processes using intelligent AI-driven tools — eliminating manual bottlenecks and accelerating throughput across operations.",
      },
      {
        icon: "ShieldCheck",
        title: "AI Governance, Risk & Compliance",
        description:
          "Implementing AI ethics frameworks, bias audits, model transparency controls, and regulatory safeguards — essential for financial services, healthcare, and other regulated industries.",
      },
      {
        icon: "LineChart",
        title: "Analytics & BI Modernization",
        description:
          "Transforming reporting and analytics capabilities — from legacy tools to AI-powered, self-service intelligence platforms with real-time dashboards and KPI frameworks.",
      },
      {
        icon: "Users",
        title: "Change Management & AI Adoption",
        description:
          "Guiding the human side of AI transformation through training programs, stakeholder alignment, and organizational change management that ensures lasting adoption — not just deployment.",
      },
      {
        icon: "Briefcase",
        title: "Executive Advisory & Performance Governance",
        description:
          "Trusted counsel for C-suite and board-level leaders — from investment governance and strategic prioritization to KPI frameworks that drive accountability across the enterprise.",
      },
      {
        icon: "Target",
        title: "Transformation Program Leadership",
        description:
          "End-to-end design and execution of large-scale modernization programs across financial services, real estate, media, and technology-enabled organizations.",
      },
    ],

    locationLine:
      "Based in Lakewood, NJ — serving enterprise clients across the Tri-State Area and nationally.",
  },

  // INDUSTRIES
  industries: {
    eyebrow: "Industries",
    title: "Proven AI expertise in the industries that move carefully — and move last.",
    intro:
      "Deep sector fluency across the industries AI disrupts most — and regulators watch closest.",
    introSecondary:
      "Proven AI expertise in the industries that move carefully — and move last.",
    items: [
      {
        number: "01",
        name: "Financial Services",
        focus:
          "AI-driven portfolio analytics, investment research copilots, risk and return modeling, and model governance for asset managers, hedge funds, wealth advisors and private equity firms under SR 11-7, the EU AI Act and emerging AI regimes.",
      },
      {
        number: "02",
        name: "Insurance",
        focus:
          "Underwriting and claims modernization, generative AI for submissions, and governance for actuarial and pricing models.",
      },
      {
        number: "03",
        name: "Healthcare & Life Sciences",
        focus:
          "Clinical AI governance, payer-provider data strategy, and responsible deployment in regulated care delivery.",
      },
      {
        number: "04",
        name: "Industrial & Manufacturing",
        focus:
          "Predictive operations, supply-chain intelligence and the data foundations required for plant-level AI at scale.",
      },
      {
        number: "05",
        name: "Energy & Utilities",
        focus:
          "Grid optimization, asset reliability, and AI strategy under evolving energy and emissions regulation.",
      },
      {
        number: "06",
        name: "Real Estate",
        focus:
          "AI-driven portfolio analytics, leasing intelligence, valuation models, and operational modernization across commercial and multifamily real estate.",
      },
      {
        number: "07",
        name: "Retail & Consumer",
        focus:
          "Demand forecasting, personalization, and generative AI for category management and customer operations.",
      },
      {
        number: "08",
        name: "Technology & Software",
        focus:
          "Productization of frontier models, evals architecture, and competitive positioning in AI-native markets.",
      },
    ],
  },

  // INSIGHTS
  insights: {
    eyebrow: "Insights",
    title: "A point of view on enterprise AI.",
    intro:
      "Perspectives, frameworks and sector-specific use cases that shape how we advise our clients.",

    hero: {
      eyebrow: "On the future of intelligence",
      quote:
        "We see a future where intelligence is a utility, like electricity or water, and people buy it from us on a meter and use it for whatever they want to use it for.",
      attribution: "Sam Altman, CEO of OpenAI",
    },

    useCasesEyebrow: "Use cases by industry",
    useCasesTitle: "Where AI creates measurable value.",
    useCasesIntro:
      "Five representative use cases across six sectors where HawkEdge advises. We offer AI adoption for almost every vertical on the market.",
    industries: [
      {
        icon: "Landmark",
        name: "Lending",
        items: [
          "AI-driven credit risk scoring using alternative and behavioral data.",
          "Automated loan origination and intelligent document processing.",
          "Early-warning models for delinquency and default prediction.",
          "KYC, AML and sanctions screening with generative AI copilots.",
          "Personalized offers and dynamic, risk-adjusted pricing.",
        ],
      },
      {
        icon: "LineChart",
        name: "Finance",
        items: [
          "Fraud detection and real-time anomaly monitoring.",
          "Forecasting for treasury, cash flow and working capital.",
          "Automated reconciliation and month-end close acceleration.",
          "Generative AI research assistants for analysts and investors.",
          "Regulatory reporting automation and compliance surveillance.",
        ],
      },
      {
        icon: "Building2",
        name: "Real Estate",
        items: [
          "Automated valuation models (AVMs) for commercial and multifamily assets.",
          "Predictive leasing analytics and tenant retention scoring.",
          "Portfolio and asset performance forecasting.",
          "Intelligent document review for leases, contracts and rent rolls.",
          "Generative AI for market research and investor reporting.",
        ],
      },
      {
        icon: "Factory",
        name: "Manufacturing",
        items: [
          "Predictive maintenance on production assets and plant equipment.",
          "Computer vision for defect detection and quality inspection.",
          "Demand forecasting and supply-chain resilience modeling.",
          "Generative design and digital-twin process simulation.",
          "Energy consumption and throughput optimization.",
        ],
      },
      {
        icon: "Stethoscope",
        name: "Health Care",
        items: [
          "Ambient clinical documentation and physician note-taking copilots.",
          "Diagnostic imaging support and triage assistance models.",
          "Predictive risk stratification for at-risk patient populations.",
          "Prior authorization, claims review and revenue-cycle automation.",
          "Operational forecasting for bed, staffing and resource utilization.",
        ],
      },
      {
        icon: "ShieldCheck",
        name: "Insurance",
        items: [
          "Generative AI submission intake and underwriting copilots.",
          "Automated claims triage, straight-through processing and FNOL.",
          "Predictive pricing, exposure modeling and portfolio optimization.",
          "Claims fraud detection and subrogation opportunity scoring.",
          "Customer service agents with retrieval-augmented policy knowledge.",
        ],
      },
    ],

    roiEyebrow: "ROI Hierarchy of AI Capabilities",
    roiTitle: "From most immediate to most strategic impact.",
    roiIntro:
      "A sequencing model for enterprise AI investment — helping leadership teams decide what to build first, and what to build to last.",
    roi: [
      {
        icon: "Workflow",
        name: "Workflow Automation",
        arrow: "Immediate cost reduction",
        body:
          "The fastest payback. Compressing routine, repeatable processes into intelligent automation — measurable in weeks, not quarters.",
      },
      {
        icon: "Sparkles",
        name: "Generative AI",
        arrow: "Productivity gains",
        body:
          "Copilots, assistants and RAG-based systems that amplify knowledge workers across research, drafting, coding and customer operations.",
      },
      {
        icon: "TrendingUp",
        name: "Predictive AI",
        arrow: "Revenue optimization",
        body:
          "Forecasting, personalization and pricing models that turn historical data into forward-looking commercial advantage.",
      },
      {
        icon: "Compass",
        name: "Decision Support",
        arrow: "Executive decision quality",
        body:
          "Analytics, simulation and scenario tooling purpose-built for the questions that reach the boardroom.",
      },
      {
        icon: "Bot",
        name: "Agentic AI",
        arrow: "Autonomous enterprise operations",
        body:
          "Multi-step, goal-directed agents that coordinate systems, data and workflows — the frontier of enterprise autonomy.",
      },
      {
        icon: "Scale",
        name: "AI Governance",
        arrow: "Risk management & regulatory compliance",
        body:
          "The foundational layer that makes everything above durable — model risk, policy, monitoring and audit under EU AI Act, NIST and sector-specific regimes.",
      },
    ],
  },

  // CONTACT
  contact: {
    eyebrow: "Contact",
    title: "Begin a conversation.",
    intro:
      "We respond to all qualified inquiries within two business days. For confidential matters, please contact us through your existing channel.",
    form: {
      submit: "Submit inquiry",
      success: "Thank you. A partner will be in touch within two business days.",
      error: "We were unable to send your message. Please try again.",
    },
  },

  footer: {
    legal:
      "© " + new Date().getFullYear() + " HawkEdge AI Advisory. All rights reserved.",
    columns: [
      {
        heading: "Practice",
        links: [
          { label: "Services", href: "/services" },
          { label: "Industries", href: "/industries" },
          { label: "Insights", href: "/insights" },
          { label: "About", href: "/" },
        ],
      },
      {
        heading: "Firm",
        links: [
          { label: "Contact", href: "/contact" },
          { label: "Careers", href: "/careers" },
        ],
      },
    ],
  },

  images: {
    hero: "https://images.unsplash.com/photo-1629655214242-03523f146285?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZyUyMG5pZ2h0fGVufDB8fHx8MTc3NzMxMDU5MHww&ixlib=rb-4.1.0&q=85",
    services:
      "https://images.unsplash.com/photo-1623428997047-296a86d28a20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZyUyMG5pZ2h0fGVufDB8fHx8MTc3NzMxMDU5MHww&ixlib=rb-4.1.0&q=85",
    philosophy:
      "https://images.unsplash.com/photo-1619976336288-38db38e4c503?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxjaGVzcyUyMHBpZWNlfGVufDB8fHx8MTc3NzMxMDU5MHww&ixlib=rb-4.1.0&q=85",
    case: "https://images.unsplash.com/photo-1760224254117-7a40f7f03fe2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB0ZXh0dXJlJTIwZGFya3xlbnwwfHx8fDE3NzczMTA1OTB8MA&ixlib=rb-4.1.0&q=85",
    team: "https://images.unsplash.com/photo-1760346546771-a81d986459ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBzdHJhdGVneSUyMG1lZXRpbmd8ZW58MHx8fHwxNzc3MzEwNTc4fDA&ixlib=rb-4.1.0&q=85",
  },
};

export default site;
